import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";
import { OndcTenantConfigStatusResponse } from "@fintechprimitives/fpapi";
import { useFpapi } from "../fpapi/useFpapi";
import { reportErrorToSentry } from "../../global/sentry/sentry";

interface OndcContextProps {
  readonly children: ReactNode;
}

interface OndcContextType {
  fetchOndcConfiguration: () => Promise<OndcTenantConfigStatusResponse | null>;
}

const OndcContext = createContext<OndcContextType | null>(null);

export const OndcContextProvider = ({ children }: OndcContextProps) => {
  const { fpapi } = useFpapi();

  const fetchOndcConfiguration = useCallback(async () => {
    try {
      const ondcConfigurationResponse = await fpapi?.fpDashboardClient
        ?.configurations()
        ?.fetchOndcTenantConfigStatus();

      return ondcConfigurationResponse || null;
    } catch (e: any) {
      reportErrorToSentry(e);
      window.debug.error(e);
      return null;
    }
  }, [fpapi?.fpDashboardClient]);

  const memoizedValue = useMemo(
    () => ({
      fetchOndcConfiguration,
    }),
    [fetchOndcConfiguration]
  );

  return <OndcContext.Provider value={memoizedValue}>{children}</OndcContext.Provider>;
};

export const useOndcContext = (): OndcContextType => {
  const context = useContext(OndcContext);
  if (!context) {
    throw new Error("useOndcContext must be used within an OndcContextProvider");
  }
  return context;
};
